import React from 'react';
import PropTypes from 'prop-types';
import chroma from 'chroma-js';
import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { darkRed, green } from '../../../styles/constants';
import dateMappings from '../../../utils/dateParts';
import { boolToNumber, getRelevantMonthsFromArch } from '../utils';


const tableStyles = StyleSheet.create({
  table: {
    display: 'table',
    width: 'auto',
    borderStyle: 'solid',
    borderColor: '#bfbfbf',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    margin: 'auto',
    flexDirection: 'row',
  },
  headerCell: {
    borderStyle: 'solid',
    borderColor: '#bfbfbf',
    borderBottomColor: '#000',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  cell: {
    borderStyle: 'solid',
    borderColor: '#bfbfbf',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  headerText: {
    margin: 5,
    fontSize: 12,
    fontWeight: 500,
  },
  text: {
    margin: 5,
    fontSize: 10,
  },
});

const PdfFloweringArch = ({ coverageGoal, archInfo, beesById }) => {
  if (!archInfo) {
    return null;
  }

  const {
    arch,
    coverage,
    minMonth,
    maxMonth,
  } = archInfo;

  if (!arch) {
    return null;
  }

  const tdColors = chroma.scale([darkRed, green])
    // +1 because we also need a color for 0 interactible plants
    .mode('lch').colors(coverageGoal + 1);

  const getTdColor = (amountInteractibleBees) => {
    // Because the tdColors array doesn't extend to infinity we take the
    // interactibleGoal as the max value.
    if (amountInteractibleBees > coverageGoal) {
      amountInteractibleBees = coverageGoal;
    }

    return tdColors[amountInteractibleBees];
  };

  const beeIds = Object.keys(arch);
  const relevantMonths = getRelevantMonthsFromArch(archInfo);

  const showBeforeMonth = dateMappings[minMonth]?.index >= 2;
  const showAfterMonth = dateMappings[maxMonth]?.index <= 21;

  const calculateColumnWidth = () => {
    // 1 is the "Dekking" column
    const totalColumns = 1
      + boolToNumber(showBeforeMonth)
      + relevantMonths.length
      + boolToNumber(showAfterMonth);

    return 90 / totalColumns;
  };

  const columnWidth = calculateColumnWidth();

  const firstHeaderCellStyle = [tableStyles.headerCell, { width: '10%' }];
  const headerCellStyle = [tableStyles.headerCell, { width: `${columnWidth}%` }];

  const firstCellStyle = [tableStyles.cell, { width: '10%' }];
  const cellStyle = [tableStyles.cell, { width: `${columnWidth}%` }];

  const renderRelevantMonthsPlaceholder = (beeId) => relevantMonths.map(
    (relevantMonth) => {
      const amountInteractibleBees = arch[beeId][relevantMonth];

      if (amountInteractibleBees === undefined) {
        return (
          <View style={cellStyle}>
            <Text style={tableStyles.text} />
          </View>
        );
      }

      const correctedAmount = amountInteractibleBees || 0;

      return (
        <View style={[cellStyle, { backgroundColor: getTdColor(correctedAmount) }]}>
          <Text style={tableStyles.text}>
            {correctedAmount}
          </Text>
        </View>
      );
    },
  );


  const renderRelevantCoverage = () => relevantMonths.map((relevantMonth) => (
    <View style={headerCellStyle}>
      <Text style={tableStyles.text}>
        {coverage.perDatepart[relevantMonth]}
        %
      </Text>
    </View>
  ));

  return (
    <View style={tableStyles.table}>
      <View style={tableStyles.tableRow}>
        <View style={firstHeaderCellStyle} />
        <View style={headerCellStyle} />
        {showBeforeMonth && (
        <View style={headerCellStyle}>
          <Text style={tableStyles.text}>[...]</Text>
        </View>
        )}
        {
          relevantMonths
            .map(
              (relevantMonth) => (dateMappings[relevantMonth].isFirstOfMonth
                ? (
                  <View style={headerCellStyle}>
                    <Text style={tableStyles.text}>
                      {dateMappings[relevantMonth].month}
                    </Text>
                  </View>
                )
                : (
                  <View style={headerCellStyle}>
                    <Text style={tableStyles.text}>
                      {/* todo: don't display anything */}
                      {dateMappings[relevantMonth].month}
                    </Text>
                  </View>
                )
              ),
            )
        }
        {showAfterMonth && (
        <View style={headerCellStyle}>
          <Text style={tableStyles.text}>[...]</Text>
        </View>
        )}
      </View>

      <View style={tableStyles.tableRow}>
        <View style={firstHeaderCellStyle} />
        <View style={headerCellStyle}>
          <Text style={tableStyles.text}>Dekking</Text>
        </View>
        {showBeforeMonth && (
          <View style={headerCellStyle}>
            <Text style={tableStyles.text}>[...]</Text>
          </View>
        )}

        {renderRelevantCoverage()}

        {showAfterMonth && (
          <View style={headerCellStyle}>
            <Text style={tableStyles.text}>[...]</Text>
          </View>
        )}
      </View>

      {beeIds.map((beeId) => (
        <View style={tableStyles.tableRow}>
          <View style={firstCellStyle}>
            <Text style={tableStyles.text}>
              {beesById[beeId].dutchName}
            </Text>
          </View>
          <View style={cellStyle}>
            <Text style={tableStyles.text}>
              {coverage.perBee[beeId]}
              %
            </Text>
          </View>

          {showBeforeMonth && (
            <View style={cellStyle}>
              <Text style={tableStyles.text} />
            </View>
          )}

          { renderRelevantMonthsPlaceholder(beeId)}

          {showAfterMonth && (
          <View style={cellStyle}>
            <Text style={tableStyles.text} />
          </View>
          )}
        </View>
      ))}
    </View>
  );
};

PdfFloweringArch.propTypes = {
  archInfo: PropTypes.shape({
    arch: PropTypes.shape(),
    coverage: PropTypes.shape(),
    minMonth: PropTypes.string,
    maxMonth: PropTypes.string,
  }),
};

PdfFloweringArch.defaultProps = {
  archInfo: null,
};

export default PdfFloweringArch;
